import React from "react";
import styled from "styled-components";
import { UI_CAPTION, UI_CAPTION_SEMIBOLD, colors2 } from "oolib";
import TooltipV2 from "@/components/UI/Tooltip";
import { useLinkedIntegrationByContentType } from "@/utils/react-query-hooks/externalConnections";
import { getIntegrationIcon, getIntegrationName } from "@/_self_managed/DirectDataImport/comps/IntegrationInterfaces";
import { CsvExcelIcon } from "@/_self_managed/DirectDataImport/comps/DataSourceSelector";

/**
 * Data provenance as source circles. Datasets are grouped by where their
 * data came from: one circle per linked integration (its icon, the same one
 * the Integrations tab uses) and one circle for every dataset that was
 * uploaded as a file. Hover names the source and lists its datasets.
 *
 * Shared by the table layout's rows, the card drill-down rows and the
 * priorities panel, so the affordance is component-level, not per-view.
 *
 * At most MAX_VISIBLE circles show; the rest fold into one "+n" circle
 * whose hover names the folded sources, so a row with many sources stays
 * one short strip everywhere.
 */
const MAX_VISIBLE = 3;

// Datasets with no linked integration were uploaded as a file.
const FILE_UPLOAD = "fileUpload";
const FILE_UPLOAD_LABEL = "CSV / Excel upload";

const CirclesWrap = styled.div`
  display: flex;
  gap: 4px;
  padding-top: 2px;
`;

const SourceCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${colors2.grey3};
  color: ${colors2.grey70};
  flex-shrink: 0;
`;

const HoverList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
`;

// One circle's hover: the source, then one line per dataset it feeds.
const SourceDatasets = ({ label, titles }) => (
  <HoverList>
    <UI_CAPTION_SEMIBOLD>{label}</UI_CAPTION_SEMIBOLD>
    {titles.map((t) => (
      <UI_CAPTION key={t}>{t}</UI_CAPTION>
    ))}
  </HoverList>
);

// The "+n" circle's hover: one line per folded source.
const MoreSources = ({ labels }) => (
  <HoverList>
    {labels.map((l) => (
      <UI_CAPTION key={l}>{l}</UI_CAPTION>
    ))}
  </HoverList>
);

/**
 * Collapse datasets into sources, in first-seen order. A dataset's source is
 * its linked integration, else the file upload.
 */
const groupBySource = (datasets, titleByContentType, linkedProviderByContentType) => {
  const bySource = new Map();
  [...new Set(datasets || [])].forEach((ct) => {
    const provider = linkedProviderByContentType[ct];
    const key = provider || FILE_UPLOAD;
    if (!bySource.has(key)) {
      bySource.set(key, {
        key,
        label: provider ? getIntegrationName(provider) : FILE_UPLOAD_LABEL,
        Icon: provider ? getIntegrationIcon(provider) : CsvExcelIcon,
        titles: [],
      });
    }
    bySource.get(key).titles.push(titleByContentType[ct] || ct);
  });
  return [...bySource.values()];
};

export const DatasetCircles = ({ datasets, titleByContentType }) => {
  const linkedProviderByContentType = useLinkedIntegrationByContentType();
  const all = groupBySource(datasets, titleByContentType, linkedProviderByContentType);
  // Fold only when there is more than one to fold — "+1" would take the
  // same room as the circle it hides.
  const folded = all.length > MAX_VISIBLE + 1 ? all.slice(MAX_VISIBLE) : [];
  const visible = folded.length ? all.slice(0, MAX_VISIBLE) : all;
  return (
    <CirclesWrap>
      {visible.map(({ key, label, Icon, titles }) => (
        // wrapperStyle keeps the tooltip's reference wrapper a flex item so
        // the circles row lays out unchanged (never wrapperDisplay="contents").
        <TooltipV2
          key={key}
          primaryContent={{ layout: "custom", render: <SourceDatasets label={label} titles={titles} /> }}
          placement="top"
          wrapperStyle={{ display: "inline-flex" }}
        >
          <SourceCircle>
            {Icon ? (
              <Icon size={16} />
            ) : (
              <UI_CAPTION style={{ color: "inherit" }}>{label.slice(0, 2).toUpperCase()}</UI_CAPTION>
            )}
          </SourceCircle>
        </TooltipV2>
      ))}
      {folded.length > 0 && (
        <TooltipV2
          primaryContent={{ layout: "custom", render: <MoreSources labels={folded.map((s) => s.label)} /> }}
          placement="top"
          wrapperStyle={{ display: "inline-flex" }}
        >
          <SourceCircle>
            <UI_CAPTION style={{ color: "inherit" }}>{`+${folded.length}`}</UI_CAPTION>
          </SourceCircle>
        </TooltipV2>
      )}
    </CirclesWrap>
  );
};
